import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { shouldAutoStart } from '../lib/tourSteps'
import WelcomeTour from './WelcomeTour'

// Decides whether the first-run tour runs, and remembers the answer. Kept
// apart from WelcomeTour so the tour itself stays a pure presentation
// component that can be rendered in a test without a database.
export default function ClientTourHost() {
  const { client, isAdmin, isPreview } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [content, setContent] = useState(null)

  // Closing the tour has to stop it coming back — not for this browser session,
  // but for good. It used to remember in sessionStorage, which is emptied when
  // the browser session ends, so anyone whose answer was never written to the
  // database met the tour again at their next sign-in. That is everybody with
  // `persist` false below: admins and previews. Skipping felt like it did
  // nothing, because by the next login it had done nothing.
  //
  // localStorage survives sign-out, and the key carries the client id so that
  // one person dismissing it on a shared machine does not hide it from the next
  // person to sign in there.
  const tourKey = client?.id ? `ac_tour_dismissed:${client.id}` : null
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!tourKey) { setDismissed(false); return }
    try { setDismissed(localStorage.getItem(tourKey) === '1') } catch { setDismissed(false) }
  }, [tourKey])

  // Previewing a client shows the tour so it can be checked, but must never
  // write to that client's row — they have not seen anything.
  const persist = !!client && !isAdmin && !isPreview

  useEffect(() => {
    let cancelled = false
    if (!client?.id) { setContent(null); return }

    async function loadCounts() {
      const count = { count: 'exact', head: true }
      const [projects, approvals, invoices, brandAssets] = await Promise.all([
        supabase.from('projects').select('id', count).eq('client_id', client.id),
        supabase.from('approvals').select('id', count).eq('client_id', client.id),
        supabase.from('invoices').select('id', count).eq('client_id', client.id),
        supabase.from('brand_assets').select('id', count).eq('client_id', client.id),
      ])
      if (cancelled) return
      // A failed count reads as zero, which hides that step rather than
      // walking someone into a screen that turns out to be empty.
      setContent({
        projects: projects.count || 0,
        approvals: approvals.count || 0,
        invoices: invoices.count || 0,
        brandAssets: brandAssets.count || 0,
      })
    }

    loadCounts()
    return () => { cancelled = true }
  }, [client?.id])

  useEffect(() => {
    if (!dismissed && content && shouldAutoStart(client, content)) setOpen(true)
  }, [client, content, dismissed])

  // "Show me around" in the sidebar reopens it after it has been dismissed.
  useEffect(() => {
    function onStart() { setDismissed(false); setOpen(true) }
    window.addEventListener('ac:start-tour', onStart)
    return () => window.removeEventListener('ac:start-tour', onStart)
  }, [])

  const close = useCallback(async (completed) => {
    setOpen(false)
    setDismissed(true)
    // Written before the network call, and independently of it: the answer has
    // to survive a failed request, a private window, and a sign-out.
    try { if (tourKey) localStorage.setItem(tourKey, '1') } catch { /* private mode */ }
    if (!persist) return
    // Skipping is an answer too: both paths record it so the tour does not
    // ambush them again on the next sign-in. The database is the authority
    // across devices; the line above covers this one.
    await supabase.rpc('mark_tour_seen', { p_completed: completed })
  }, [persist, tourKey])

  if (!client || !content) return null

  return (
    <WelcomeTour
      open={open}
      content={content}
      clientName={client.name}
      onFinish={() => close(true)}
      onSkip={() => close(false)}
      onAskQuestion={() => { close(true); navigate('/messages') }}
    />
  )
}
